import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message } from "antd";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import { IPoRow, IPoUpload } from "common/interface";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { removeProcessingBillingRate, setProcessingBillingRate, } from "store/po/poSlice";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";

const POBillingRateUploadPage = () => {
  useDocumentTitle("Upload Billing Rate");
  const { authToken } = useAppSelector((state) => 
  state.app.user);
  const { processingBillingRate } = useAppSelector((state) => state.po);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);

  useEffect(() => {
    const { timeUploaded } = processingBillingRate;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingBillingRate());
      }
    }

    if (processingBillingRate && processingBillingRate.key) {
      navigate(`/${routes.PO.SAVE_BILLING_RATE}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingBillingRate, navigate, dispatch]);

  useEffect(() => {
    const failed = processingBillingRate?.failedRows?.length;

    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingBillingRate?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IPoUpload) => {
    dispatch(setProcessingBillingRate(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingBillingRate());
  };

  const { headerMapping } = processingBillingRate;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingBillingRate?.successRows || [];
  const failedRows = processingBillingRate?.failedRows || [];
  const successData = successRows?.map((row, i) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows?.map((row, i) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];
  const columns: ColumnsType<IPoRow & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: IPoRow & { key: string }) => {
        if (key === "errors") {
          const errorMessages = record.errors
            ?.map((error) => error.message)
            .join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: IPoRow & { key: string }) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }

    return "";
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Billing Rate" />
        </Col>
      </Row>

      <Row>
        <Col lg={16} xs={24}>
          <AppUpload
            uploadUrl={apis.uploadBillingRate}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
          />
        </Col>
      </Row>

      {!!Object.keys(processingBillingRate).length && !processingBillingRate.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>

          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={(record) => addBorderToErrorRows(record)}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            ></Table>
          </Row>
        </>
      )}
    </>
  );
};

export default POBillingRateUploadPage;
